/**
 * Onde um cartão de DADOS é entregue: `inline` (na bolha, como sempre foi) ou
 * `painel` (prévia clicável + aside do painel de artefato).
 *
 * ★ A decisão é do BACKEND (decisão do usuário, 14/09/2026): a tool sabe o
 *   tamanho real da resposta, e a régua por cartão/tamanho vive lá — chega
 *   ao front como `ui.apresentacao: 'inline' | 'painel'` na parte da tool
 *   (opcional). O front NÃO tem tabela cartão → modo nem atributo por entrada
 *   no registry; ele só aplica DUAS regras estruturais, que não são de
 *   produto e que o backend não tem como saber:
 *
 *   1. `painelDisponivel` falso ⇒ `inline`. É o getter
 *      `<storeModule>/modoArtefatoAtivo` (kill-switch `ARTEFATO_MODO_ATIVO` E
 *      `modoAtivo` da imersiva): fora da `/home-ia`, ou numa superfície sem o
 *      slice (Fee Fixo, ai-framework), não existe aside para abrir.
 *   2. cartão de ESCRITA (`entrada.states` ∋ `approval-requested`) ⇒ `inline`.
 *      O aside monta `<component :is v-bind="props">` sem os atributos de
 *      MOLDURA — `state`, `tool-call-id`, `turno-em-voo`, `somente-leitura`
 *      (`artefato-painel.vue`). ★ O `@respond` deixou de faltar em
 *      17/09/2026 (o painel o sobe para `view-chat.responderCartao`, o mesmo
 *      destino da bolha), mas sozinho não basta: sem `tool-call-id` a
 *      resposta sai sem a identidade do preview, e sem `state` o cartão não
 *      desenha a fase. Rever esta régua é decisão do owner.
 *   3. `parte.ui.apresentacao` dentro do enum ⇒ esse valor.
 *   4. ausente (ou fora do enum) ⇒ `APRESENTACAO_PADRAO` (`painel`) — o
 *      comportamento de antes deste arquivo, para o backend que ainda não
 *      manda o campo continuar como estava.
 *
 * Valor fora do enum é IGNORADO (cai no 4), não recusado: um typo no backend
 * não pode virar `fallback-card` numa resposta que já chegou certa.
 *
 * Puro de propósito — sem store, sem router; o `painelDisponivel` chega por
 * argumento (o mixin `renderiza-partes.js` é quem lê o getter). O único import
 * é o vocabulário de estado (`TOOL_PART_STATE`), para não redigitar a string.
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila). Acentos UTF-8 reais.
 */
import { TOOL_PART_STATE } from '@modules/aawz-intelligence-v2/store/toolParts'

export const APRESENTACAO = Object.freeze({
  INLINE: 'inline',
  PAINEL: 'painel',
})

export const APRESENTACOES = Object.freeze(Object.values(APRESENTACAO))

// Sem o campo do backend, o cartão de dados vai ao painel — é o que ele
// fazia antes de `ui.apresentacao` existir.
export const APRESENTACAO_PADRAO = APRESENTACAO.PAINEL

/**
 * A entrada do registry desenha uma fase de CONFIRMAÇÃO (`approval-requested`)
 * — é um cartão de escrita, que o aside ainda não sabe desenhar (ver a régua
 * 2 no cabeçalho).
 */
export function ehCartaoDeEscrita(entrada) {
  const states = entrada && entrada.states
  if (!Array.isArray(states)) return false
  return states.includes(TOOL_PART_STATE.APROVACAO)
}

/** O `ui.apresentacao` da parte, ou `null` quando ausente ou fora do enum. */
function apresentacaoDoBackend(parte) {
  const ui = parte && parte.ui
  const valor = ui && ui.apresentacao
  if (!APRESENTACOES.includes(valor)) return null
  return valor
}

/**
 * A régua — ver o cabeçalho. Devolve sempre um valor de `APRESENTACAO`.
 *
 * @param {{ painelDisponivel: boolean, entrada: object, parte: object }} args
 * @returns {'inline' | 'painel'}
 */
export function resolverApresentacao({ painelDisponivel, entrada, parte } = {}) {
  if (!painelDisponivel) return APRESENTACAO.INLINE
  if (ehCartaoDeEscrita(entrada)) return APRESENTACAO.INLINE
  const doBackend = apresentacaoDoBackend(parte)
  if (doBackend !== null) return doBackend
  return APRESENTACAO_PADRAO
}
